<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import AssistantPlayground from 'dashboard/components-next/captain/assistant/AssistantPlayground.vue';
import PlaygroundDebugPanel from 'dashboard/components-next/captain/assistant/PlaygroundDebugPanel.vue';

const route = useRoute();
const assistantId = computed(() => Number(route.params.assistantId));

// Key used to persist logs in localStorage per assistant
const storageKey = computed(
  () => `captain_playground_logs_${assistantId.value}`
);

// Load persisted logs from localStorage for this assistant
const loadLogs = () => {
  try {
    const raw = localStorage.getItem(storageKey.value);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const logs = ref(loadLogs());

// When assistant switches, reload logs for new assistant
watch(assistantId, () => {
  logs.value = loadLogs();
});

const saveLogs = () => {
  try {
    const trimmed = logs.value.slice(-100);
    localStorage.setItem(storageKey.value, JSON.stringify(trimmed));
  } catch {
    // localStorage may be full — fail silently
  }
};

const handleLogEntry = entry => {
  logs.value.push(entry);
  saveLogs();
};

const clearLogs = () => {
  logs.value = [];
  try {
    localStorage.removeItem(storageKey.value);
  } catch {
    // fail silently
  }
};
</script>

<template>
  <PageLayout
    show-assistant-switcher
    :show-pagination-footer="false"
    :show-know-more="false"
    class="h-full"
  >
    <template #body>
      <!--
        PageLayout wraps the body slot in: max-w-5xl h-full mx-auto py-4
        We use -mx-6 -my-4 to break out of that padding, then set our own.
      -->
      <div class="-mx-6 -my-4 h-[calc(100%+2rem)] flex gap-4 px-4 py-4">
        <!-- Chat playground (left, flexible) -->
        <div class="flex-1 min-w-0">
          <AssistantPlayground
            :assistant-id="assistantId"
            class="bg-n-solid-1 h-full"
            @log-entry="handleLogEntry"
          />
        </div>

        <!-- Debug log panel (right, 320px) -->
        <div class="w-80 flex-shrink-0">
          <PlaygroundDebugPanel
            :assistant-id="assistantId"
            :logs="logs"
            class="h-full"
            @clear-logs="clearLogs"
          />
        </div>
      </div>
    </template>
  </PageLayout>
</template>
